import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, toggleWidgetTheme, updateSelectedSymbolInDropdown, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

export class TraderverseMarketNewsPublishers {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private interval: '1d' | '5d' | '1m' = '1m';
  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    await this.injectChartLibrary();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const symbolOptions = this.fetchSymbols()
      .map(s => `<option value="${s.value}">${s.label}</option>`).join('');

    const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}" data-theme="${this.config.theme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} News Publishers</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
              
              ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                              <div class="trader-sm-card" id="selected-symbol">
                              ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
                              </div>
                              <span id="symbol-dropdown-placeholder"></span>
                              </div>`}
              <span></span>
              <div class="item">
                <select class="interval-dropdown">
              <option value="1d">1 Day</option>
              <option value="5d" selected>5 Days</option>
              <option value="1m">1 Month</option>
              </select>
              </div>                
            </div>

            <div class="chartDv" id="marketNewsContainer">
            </div>

          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    const intervalDropdown = this.shadowRoot.querySelector('.interval-dropdown') as HTMLSelectElement;

    symbolDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.config.symbol = value;
      this.setupChart(value);
    });

    intervalDropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.interval = value as '1d' | '5d' | '1m';
      this.setupChart(this.config.symbol!);
    });

    if (symbolDropdown && this.config.symbol) symbolDropdown.value = this.config.symbol;
    if (intervalDropdown) intervalDropdown.value = this.interval || '5d';
  }



  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      this.currentData = [
        {
          publisher: "CNN News",
          newsCount: 596,
          bars: [30, 30, 40],
          img: "https://traderboard.traderverse.io/img/np-01.00627905.png"
        },
        {
          publisher: "Bloomberg",
          newsCount: 430,
          bars: [25, 35, 40],
          img: "https://traderboard.traderverse.io/img/np-02.1a188801.png"
        },
        {
          publisher: "Reuters",
          newsCount: 512,
          bars: [20, 40, 40],
          img: "https://traderboard.traderverse.io/img/np-03.6f7cdf34.png"
        },
        {
          publisher: "Yahoo Finance",
          newsCount: 388,
          bars: [30, 30, 40],
          img: "https://traderboard.traderverse.io/img/np-04.042ed51d.png"
        },
        {
          publisher: "MarketWatch",
          newsCount: 275,
          bars: [30, 30, 40],
          img: "https://traderboard.traderverse.io/img/np-05.8d70e582.png"
        },
        {
          publisher: "WSJ",
          newsCount: 320,
          bars: [30, 30, 40],
          img: "https://traderboard.traderverse.io/img/np-06.a0d514d3.png"
        }
      ];

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);

    } catch (err) {
      console.error("Error loading dummy chart data:", err);
    }
  }




  private updateDisplay(): void {
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderPortfolioAccount();
  }


  private renderPortfolioAccount(): void {
    const container = this.shadowRoot.querySelector('#marketNewsContainer');
    if (!container) return;

const hasData =
        this.currentData &&
        Array.isArray(this.currentData) &&
        this.currentData.length > 0;

    if (hasData) {
        container.setAttribute('data-testing-id', 'market-news-container');
    } else {
        container.removeAttribute('data-testing-id');
        container.innerHTML = getNoDataHTML();
        return;
    }

    const html = `
   <div class="newsPublishersMain">
        <div class="item new">
            <figure><img src="https://traderboard.traderverse.io/img/np-01.00627905.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item new">
            <figure><img src="https://traderboard.traderverse.io/img/np-02.1a188801.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item">
            <figure><img src="https://traderboard.traderverse.io/img/np-03.6f7cdf34.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item">
            <figure><img src="https://traderboard.traderverse.io/img/np-04.042ed51d.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item">
            <figure><img src="https://traderboard.traderverse.io/img/np-05.8d70e582.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item new">
            <figure><img src="https://traderboard.traderverse.io/img/np-06.a0d514d3.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item new">
            <figure><img src="https://traderboard.traderverse.io/img/np-01.00627905.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item new">
            <figure><img src="https://traderboard.traderverse.io/img/np-02.1a188801.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item">
            <figure><img src="https://traderboard.traderverse.io/img/np-03.6f7cdf34.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item">
            <figure><img src="https://traderboard.traderverse.io/img/np-04.042ed51d.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item">
            <figure><img src="https://traderboard.traderverse.io/img/np-05.8d70e582.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
        <div class="item new">
            <figure><img src="https://traderboard.traderverse.io/img/np-06.a0d514d3.png"/></figure>
            <h3>CNN News</h3>
            <p>596 News</p>
            <div class="rangeBar">
                <span style="width: 30%;"></span>
                <span style="width: 30%;"></span>
                <span style="width: 40%;"></span>
            </div>
        </div>
    </div>
    `;
    container.innerHTML = html;
  }





  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseMarketNewsPublishers: typeof TraderverseMarketNewsPublishers; } }
if (typeof window !== 'undefined') { window.TraderverseMarketNewsPublishers = TraderverseMarketNewsPublishers; }
